import { c as _c } from "react/compiler-runtime";
import request from '@onefootprint/request';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { AuthHeaders } from '@/hooks/use-session';
import useSession from '@/hooks/use-session';
import { useSwitchTenant } from '@/hooks/use-switch-tenant/use-switch-tenant';
export type LookupObjectContext = {
  object: 'scoped_vault';
  fpId: string;
  kind: 'person' | 'business';
} | {
  object: 'playbook';
  playbookId: string;
  versionId: string;
} | {
  object: 'agent';
  agentId: string;
} | {
  object: 'chat_session';
  chatSessionId: string;
};
export type LookupObjectResponse = {
  tenantId: string;
  isLive: boolean | null;
  context: LookupObjectContext;
};
const lookupObject = async (authHeaders: AuthHeaders, id: string) => {
  const {
    data
  } = await request<LookupObjectResponse>({
    method: 'GET',
    url: 'private/lookup_object',
    headers: authHeaders,
    params: {
      id
    }
  });
  return data;
};

/**
 * Firm-employee-only: resolves any object id (playbook / agent / entity) to its owning
 * tenant via `GET /private/lookup_object`, then assumes that tenant if not already in it.
 * `onSwitched(data, didSwitch)` runs after the switch — `didSwitch` is true when we actually
 * changed tenants (e.g. so an imperatively-loaded page can hard-refresh to pick up the new
 * auth token). Use it for resource-specific route rewrites / reloads.
 */
const useAssumeToViewObject = (id, onSwitched) => {
  const $ = _c(20);
  const {
    authHeaders,
    data: t0
  } = useSession();
  const {
    user
  } = t0;
  const isFirmEmployee = !!user?.isFirmEmployee;
  const switchTenant = useSwitchTenant();
  let t1;
  if ($[0] !== id) {
    t1 = ["assume", "object", id];
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== authHeaders || $[3] !== id) {
    t2 = () => lookupObject(authHeaders, id as string);
    $[2] = authHeaders;
    $[3] = id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = !!id && isFirmEmployee;
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const query = useQuery(t4);
  let t5;
  if ($[9] !== onSwitched || $[10] !== switchTenant) {
    t5 = async data => {
      const {
        didSwitch
      } = await switchTenant({
        tenantId: data.tenantId,
        isLive: data.isLive ?? undefined
      });
      onSwitched?.(data, didSwitch);
    };
    $[9] = onSwitched;
    $[10] = switchTenant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleSwitchTenant = t5;
  const assumedIdRef = useRef(undefined);
  let t6;
  if ($[12] !== handleSwitchTenant || $[13] !== id || $[14] !== query.data || $[15] !== query.isSuccess) {
    t6 = () => {
      if (!id) {
        assumedIdRef.current = undefined;
      }
      if (!query.isSuccess || !query.data || assumedIdRef.current === id) {
        return;
      }
      assumedIdRef.current = id;
      handleSwitchTenant(query.data);
    };
    $[12] = handleSwitchTenant;
    $[13] = id;
    $[14] = query.data;
    $[15] = query.isSuccess;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== id || $[18] !== query.isSuccess) {
    t7 = [query.isSuccess, id];
    $[17] = id;
    $[18] = query.isSuccess;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  useEffect(t6, t7);
  return query;
};
export default useAssumeToViewObject;